// Registers eval-free polyfills for WebGL shader compilation.
// Required for environments with strict CSP (no unsafe-eval), e.g. when
// the widget is injected via a browser extension into third-party pages.
import 'pixi.js/unsafe-eval'
import { Application } from 'pixi.js'

export interface StageOptions {
  /** Width of the PixiJS canvas in CSS pixels */
  width: number
  /** Height of the PixiJS canvas in CSS pixels */
  height: number
}

const STAGE_WIDTH = 480
const STAGE_HEIGHT = 480

export type StageMode = 'floating' | 'inline'

export class WidgetStage {
  app: Application
  private container: HTMLDivElement | null = null
  private resizeObserver: ResizeObserver | null = null
  private mode: StageMode = 'floating'
  private targetEl: HTMLElement | null = null

  constructor() {
    this.app = new Application()
  }

  /**
   * Initialize the stage.
   * @param mode 'floating' (fixed position) or 'inline' (inside a target container)
   * @param targetSelector CSS selector for the target container (inline mode only)
   */
  async init(mode: StageMode = 'floating', targetSelector?: string | null): Promise<HTMLCanvasElement> {
    this.mode = mode

    // In inline mode, find the target container
    if (mode === 'inline' && targetSelector) {
      this.targetEl = document.querySelector(targetSelector)
      if (!this.targetEl) {
        console.warn(`[HolostaffWidget] Container "${targetSelector}" not found, falling back to floating mode`)
        this.mode = 'floating'
      }
    }

    await this.app.init({
      width: STAGE_WIDTH,
      height: STAGE_HEIGHT,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      // Force WebGL — pixi.js/unsafe-eval patches the WebGL renderer's eval check.
      // Without this, PixiJS v8 tries WebGPU first, whose eval check is unpatched
      // and throws in CSP-restricted environments (e.g. browser extension injection).
      preference: 'webgl',
    })

    this.container = document.createElement('div')
    this.container.id = 'holostaff-widget-root'

    if (this.mode === 'inline' && this.targetEl) {
      // Inline mode: render inside the target container
      Object.assign(this.container.style, {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'visible',
        pointerEvents: 'none',
      })
      this.targetEl.style.position = 'relative'
      this.targetEl.style.cursor = 'pointer'
      this.targetEl.appendChild(this.container)
    } else {
      // Floating mode: fixed position at bottom of viewport
      Object.assign(this.container.style, {
        position: 'fixed',
        bottom: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${STAGE_WIDTH}px`,
        height: `${STAGE_HEIGHT}px`,
        zIndex: '2147483646',
        pointerEvents: 'none',
        overflow: 'visible',
      })
      // Append to <html> rather than <body> to escape any CSS transforms that
      // JS animation libraries (e.g. GSAP) may apply to <body> or scroll containers.
      // A transform on an ancestor breaks position:fixed (makes it relative to that
      // ancestor instead of the viewport), causing the widget to scroll with the page.
      document.documentElement.appendChild(this.container)
    }

    const canvas = this.app.canvas as HTMLCanvasElement
    Object.assign(canvas.style, {
      width: '100%',
      height: '100%',
      display: 'block',
      pointerEvents: 'auto',
    })

    this.container.appendChild(canvas)
    this.setupResize()

    return canvas
  }

  private setupResize(): void {
    const onResize = () => {
      if (this.mode === 'inline' && this.targetEl) {
        // In inline mode, size is driven by the target container
        const rect = this.targetEl.getBoundingClientRect()
        if (this.container) {
          this.container.style.width = `${rect.width}px`
          this.container.style.height = `${rect.height}px`
        }
      } else {
        // Floating mode: clamp to viewport width
        const vw = window.innerWidth
        const width = Math.min(STAGE_WIDTH, vw)
        const scale = width / STAGE_WIDTH
        const height = STAGE_HEIGHT * scale

        if (this.container) {
          this.container.style.width = `${width}px`
          this.container.style.height = `${height}px`
        }
      }

      this.app.renderer.resize(STAGE_WIDTH, STAGE_HEIGHT)
    }

    window.addEventListener('resize', onResize)

    // Also observe the target container for size changes in inline mode
    if (this.mode === 'inline' && this.targetEl) {
      this.resizeObserver = new ResizeObserver(onResize)
      this.resizeObserver.observe(this.targetEl)
    }

    onResize()
  }

  /** Whether the stage is in inline mode. */
  get isInline(): boolean {
    return this.mode === 'inline' && this.targetEl !== null
  }

  /** The target container element (inline mode only). */
  get inlineTarget(): HTMLElement | null {
    return this.targetEl
  }

  /** The root DOM container for adding sibling elements (e.g. idle video). */
  get rootElement(): HTMLDivElement | null {
    return this.container
  }

  get stageWidth(): number {
    return STAGE_WIDTH
  }

  get stageHeight(): number {
    return STAGE_HEIGHT
  }

  /**
   * Get the bounding rect of the inline target container.
   * Used for positioning the conversation popover.
   */
  getInlineRect(): DOMRect | null {
    return this.targetEl?.getBoundingClientRect() ?? null
  }

  destroy(): void {
    window.removeEventListener('resize', this.setupResize)
    this.resizeObserver?.disconnect()
    this.container?.remove()
    this.app.destroy(true)
  }
}
